import { AdvancedSearchURLParams } from '@ltvco/refresh-lib/v2';
import React, { useEffect, useRef } from 'react';

import { type OpinionResult } from 'routes/Dashboard/CourtCases';
import { useCourtSearch } from 'routes/Dashboard/CourtCases/api';

interface CourtOpinionFetcherProps {
  searchData: AdvancedSearchURLParams;
  // eslint-disable-next-line no-unused-vars
  setCourtOpinionResults: (results: OpinionResult[]) => void;
  /** Notify parent when the court opinions search is in progress (so UI can show loading instead of stale/wrong data) */
  onLoadingChange?: (loading: boolean) => void;
}

export function CourtOpinionFetcher({
  searchData,
  setCourtOpinionResults,
  onLoadingChange,
}: CourtOpinionFetcherProps) {
  const query =
    [searchData.fName, searchData.mName, searchData.lName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    searchData.fullName ||
    searchData.case_name ||
    '';

  const { opinionsResults, triggerCombinedSearch, isSearching } =
    useCourtSearch('', false);

  const lastResultsKeyRef = useRef<string | null>(null);
  const lastQueryRef = useRef<string>('');

  useEffect(() => {
    const trimmedQuery = query.trim();
    if (trimmedQuery && trimmedQuery !== lastQueryRef.current) {
      lastQueryRef.current = trimmedQuery;
      // Reset results key when starting a new search
      lastResultsKeyRef.current = null;
      triggerCombinedSearch(trimmedQuery, {});
    }
  }, [query, triggerCombinedSearch]);

  useEffect(() => {
    // Only process results when search is complete
    if (!isSearching) {
      const currentResultsKey = opinionsResults?.results?.length
        ? opinionsResults.results
            .map((r) => `${r.cluster_id}-${r.docket_id}-${r.docketNumber}`)
            .join(',')
        : '';

      // Only update if the actual results have changed
      if (currentResultsKey !== lastResultsKeyRef.current) {
        lastResultsKeyRef.current = currentResultsKey;

        if (opinionsResults?.results && opinionsResults.results.length > 0) {
          setCourtOpinionResults(opinionsResults.results);
        } else {
          setCourtOpinionResults([]);
        }
      }
    }
  }, [opinionsResults, isSearching, setCourtOpinionResults]);

  useEffect(() => {
    onLoadingChange?.(isSearching);
  }, [isSearching, onLoadingChange]);

  return <React.Fragment />;
}
